import { ElementCategoryID } from '@shared/models/element-categories.model';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { Box, SxProps, Theme } from '@mui/material';
import { IconButton } from '@ui/buttons';
import { Tooltip } from '@ui/Tooltip';
import { Icon } from '@ui/Icon';
import { ElementCategoryMenu } from '@features/elements/components/ElementCategoryMenu/ElementCategoryMenu';
import { ICategoryGroup } from '../../hooks/use-grouped-element-categories.hook';
import {
  CategoryType,
  getElementCategory,
} from '@shared/helpers/element-category.helpers';

type ElementCategoryMenuIconButtonProps = {
  categoryGroups: ICategoryGroup<{
    disabled: false | string;
    type: CategoryType;
  }>[];
  disabled?: boolean;
  /** Blocks opening the menu without applying disabled button styling. */
  categoryLocked?: boolean;
  selectedCategoryId?: ElementCategoryID;
  onSelect: (categoryId: ElementCategoryID) => void;
};

export const ElementCategoryMenuIconButton: React.FC<
  ElementCategoryMenuIconButtonProps
> = ({
  selectedCategoryId,
  categoryGroups,
  disabled = false,
  categoryLocked = false,
  onSelect,
}) => {
  const categoryId = selectedCategoryId ?? ElementCategoryID.None;
  const iconName = getElementIconCategoryName(categoryId);
  const tooltipTitle = useMemo(() => {
    const categoryName = getElementCategory(categoryId)?.name;
    if (!categoryName) return 'Element type';
    return `Element type: ${categoryName}`;
  }, [categoryId]);
  const showStaticIcon = categoryLocked && !disabled;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const closeMenu = useCallback(() => setAnchorEl(null), []);
  const openMenu = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (categoryLocked) return;
      setAnchorEl(e.currentTarget);
    },
    [categoryLocked],
  );

  const selectCategory = (categoryId: ElementCategoryID) => {
    onSelect(categoryId);
    closeMenu();
  };

  return (
    <>
      <Tooltip title={tooltipTitle} showWhenDisabled>
        {showStaticIcon ? (
          <Box component="span" sx={staticCategoryIconSx}>
            <Icon category="elementCategory" icon={iconName} />
          </Box>
        ) : (
          <IconButton
            category="elementCategory"
            shape="square"
            icon={iconName}
            disabled={disabled}
            onClick={openMenu}
          />
        )}
      </Tooltip>
      {!categoryLocked && (
        <ElementCategoryMenu
          anchor={anchorEl}
          selectedCategoryId={selectedCategoryId}
          categoryGroups={categoryGroups}
          onSelect={selectCategory}
          onClose={closeMenu}
        />
      )}
    </>
  );
};

/** Matches small square {@link IconButton} footprint without interactive affordances. */
const staticCategoryIconSx: SxProps<Theme> = {
  alignItems: 'center',
  borderRadius: (theme) => `${theme.shape.borderRadius}px`,
  cursor: 'default',
  display: 'inline-flex',
  height: 30,
  justifyContent: 'center',
  width: 30,
};
